import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  type AdminUserRow,
  type ObjectScopeStateResponse,
  type ObjectRow,
  type PermissionGroupRow,
  type PermissionGroupStateResponse,
  type PermissionOverrideValue,
  type RolePermissionKey
} from "../app/api";
import type { AuthUser } from "../app/auth";
import { DeleteCodeConfirmModal } from "../components/common/DeleteCodeConfirmModal";
import { ServerDataTable } from "../components/tables/ServerDataTable";
import { isActionMenuEventTarget, useActionMenuAutoClose } from "../hooks/useActionMenuAutoClose";

type AdminUsersPageProps = {
  authUser: AuthUser;
};

const SearchField = ({
  placeholder,
  value,
  onChange
}: {
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) => (
  <div className="templates-v2-search-field admin-users-search-field">
    <span className="templates-v2-search-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none">
        <circle cx="11.2" cy="11.2" r="5.7" stroke="currentColor" strokeWidth="1.8" />
        <path d="m15.4 15.4 3.2 3.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </span>
    <input placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export const AdminUsersPage = ({ authUser }: AdminUsersPageProps) => {
  const canCreateUsers = authUser.permissions.admin_users_create !== false;
  const canUpdateUsers = authUser.permissions.admin_users_update !== false;
  const canDeleteUsers = authUser.permissions.admin_users_delete !== false;
  const canEditRoleMatrix = canUpdateUsers;
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [createError, setCreateError] = useState("");
  const [editError, setEditError] = useState("");
  const [editRow, setEditRow] = useState<AdminUserRow | null>(null);
  const [createAvatarFile, setCreateAvatarFile] = useState<File | null>(null);
  const [editAvatarFile, setEditAvatarFile] = useState<File | null>(null);
  const [editAvatarRemoved, setEditAvatarRemoved] = useState(false);
  const [createAvatarDragOver, setCreateAvatarDragOver] = useState(false);
  const [editAvatarDragOver, setEditAvatarDragOver] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [actionMenuPosition, setActionMenuPosition] = useState<{ left: number; top: number } | null>(null);
  const [isActionMenuHovered, setIsActionMenuHovered] = useState(false);
  const createAvatarInputRef = useRef<HTMLInputElement | null>(null);
  const editAvatarInputRef = useRef<HTMLInputElement | null>(null);

  const [createForm, setCreateForm] = useState({
    username: "",
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    displayName: "",
    groupId: "",
    isPlatformAdmin: false,
    password: ""
  });

  const [editForm, setEditForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    displayName: "",
    groupId: "",
    isPlatformAdmin: false,
    status: "aktiv" as "aktiv" | "inaktiv",
    password: ""
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => api.adminUsers()
  });
  const { data: permissionGroupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ["permission-groups"],
    queryFn: () => api.permissionGroups()
  });
  const { data: objectScopeData } = useQuery({
    queryKey: ["object-scope"],
    queryFn: () => api.objectScope()
  });
  const { data: objectsData } = useQuery({
    queryKey: ["objects-for-scope"],
    queryFn: () => api.objects({ page: 1, pageSize: 5000, sortBy: "objektnummer", sortDir: "asc" })
  });

  useEffect(() => {
    if (!openActionMenuId) return;
    const closeMenu = (event?: MouseEvent) => {
      if (event && isActionMenuEventTarget(event.target)) return;
      setOpenActionMenuId(null);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    window.addEventListener("mousedown", closeMenu);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", closeMenu);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [openActionMenuId]);

  useEffect(() => {
    if (!openActionMenuId) return;
    const closeMenu = () => setOpenActionMenuId(null);
    window.addEventListener("resize", closeMenu);
    window.addEventListener("scroll", closeMenu, true);
    return () => {
      window.removeEventListener("resize", closeMenu);
      window.removeEventListener("scroll", closeMenu, true);
    };
  }, [openActionMenuId]);

  useActionMenuAutoClose({
    isOpen: !!openActionMenuId,
    isHovered: isActionMenuHovered,
    closeMenu: () => setOpenActionMenuId(null),
    timeoutMs: 3000
  });

  useEffect(() => {
    if (!openActionMenuId) {
      setActionMenuPosition(null);
      setIsActionMenuHovered(false);
    }
  }, [openActionMenuId]);

  const [groupsDraft, setGroupsDraft] = useState<PermissionGroupStateResponse["state"] | null>(null);
  const [objectScopeDraft, setObjectScopeDraft] = useState<ObjectScopeStateResponse["state"] | null>(null);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");
  const [newGroupActive, setNewGroupActive] = useState(true);
  const [showGroupCreate, setShowGroupCreate] = useState(false);
  const [groupsMessage, setGroupsMessage] = useState("");
  const [groupsError, setGroupsError] = useState("");
  const [scopeMessage, setScopeMessage] = useState("");
  const [scopeError, setScopeError] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [userGroupFilter, setUserGroupFilter] = useState("");
  const [userStatusFilter, setUserStatusFilter] = useState<"" | "aktiv" | "inaktiv">("");
  const [groupSearch, setGroupSearch] = useState("");
  const [groupStatusFilter, setGroupStatusFilter] = useState<"" | "aktiv" | "inaktiv">("");
  const [groupEditId, setGroupEditId] = useState("");
  const [groupEditName, setGroupEditName] = useState("");
  const [groupEditDescription, setGroupEditDescription] = useState("");
  const [groupEditActive, setGroupEditActive] = useState(true);
  const [groupEditError, setGroupEditError] = useState("");
  const [assignmentSearch, setAssignmentSearch] = useState("");
  const [assignmentPage, setAssignmentPage] = useState(1);
  const [scopeObjectSearch, setScopeObjectSearch] = useState("");
  const [scopeObjectPage, setScopeObjectPage] = useState(1);
  const [userDeleteConfirm, setUserDeleteConfirm] = useState<{
    id: string;
    label: string;
    code: string;
    input: string[];
    error: string;
  } | null>(null);
  const [groupDeleteConfirm, setGroupDeleteConfirm] = useState<{
    id: string;
    label: string;
    code: string;
    input: string[];
    error: string;
  } | null>(null);

  const createSecurityCode = () => String(Math.floor(1000 + Math.random() * 9000));
  const openActionMenuAtTrigger = (menuId: string, trigger: HTMLElement) => {
    const rect = trigger.getBoundingClientRect();
    const menuWidth = 170;
    const menuHeight = 96;
    const margin = 8;
    const left = Math.min(
      Math.max(margin, rect.right - menuWidth),
      Math.max(margin, window.innerWidth - menuWidth - margin)
    );
    const belowTop = rect.bottom + 6;
    const aboveTop = rect.top - menuHeight - 6;
    const top = belowTop + menuHeight <= window.innerHeight - margin ? belowTop : Math.max(margin, aboveTop);
    setActionMenuPosition({ left, top });
    setOpenActionMenuId(menuId);
  };
  const normalizeAvatarFile = (file: File | null) =>
    file && String(file.type ?? "").toLowerCase().startsWith("image/") ? file : null;
  const getUserInitials = (r: AdminUserRow) => {
    const first = String(r.firstName ?? "").trim();
    const last = String(r.lastName ?? "").trim();
    const fromNames = `${first.slice(0, 1)}${last.slice(0, 1)}`.toUpperCase();
    if (fromNames) return fromNames;
    const fallback = String(r.displayName || r.username || "").trim();
    const parts = fallback.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return fallback.slice(0, 2).toUpperCase() || "--";
  };

  const permissionKeys = (permissionGroupsData?.keys ?? []) as RolePermissionKey[];
  const isPlatformRightsContext = permissionKeys.includes("platform_tenants");
  const supportsObjectScope = permissionKeys.includes("objects");
  const effectiveGroupState = groupsDraft ?? permissionGroupsData?.state ?? { groups: [], userAssignments: {} };
  const effectiveObjectScope = objectScopeDraft ?? objectScopeData?.state ?? { userAssignments: {} };
  const isFrameworkV2 = typeof document !== "undefined" && document.body.classList.contains("framework-v2");

  const groupsMutation = useMutation({
    mutationFn: (state: PermissionGroupStateResponse["state"]) => api.updatePermissionGroups(state),
    onSuccess: async (next) => {
      setGroupsError("");
      setGroupsMessage("Benutzergruppe gespeichert.");
      setGroupsDraft(next.state);
      await queryClient.invalidateQueries({ queryKey: ["permission-groups"] });
    },
    onError: (err) => {
      setGroupsMessage("");
      setGroupsError(err instanceof Error ? err.message : "Usergruppen konnten nicht gespeichert werden");
    }
  });
  const objectScopeMutation = useMutation({
    mutationFn: (state: ObjectScopeStateResponse["state"]) => api.updateObjectScope(state),
    onSuccess: async (next) => {
      setScopeError("");
      setScopeMessage("Objektzuordnung gespeichert.");
      setObjectScopeDraft(next.state);
      await queryClient.invalidateQueries({ queryKey: ["object-scope"] });
    },
    onError: (err) => {
      setScopeMessage("");
      setScopeError(err instanceof Error ? err.message : "Objektzuordnung konnte nicht gespeichert werden");
    }
  });

  const createMutation = useMutation({
    mutationFn: () =>
      api.createAdminUser({
        username: createForm.username.trim(),
        email: createForm.email.trim().toLowerCase(),
        firstName: createForm.firstName.trim(),
        lastName: createForm.lastName.trim(),
        phone: createForm.phone.trim() || undefined,
        displayName: createForm.displayName.trim() || `${createForm.firstName.trim()} ${createForm.lastName.trim()}`.trim(),
        groupId: createForm.isPlatformAdmin ? undefined : createForm.groupId,
        isPlatformAdmin: createForm.isPlatformAdmin,
        password: createForm.password
      }),
    onSuccess: async (createdUser) => {
      if (createAvatarFile) {
        await api.uploadAdminUserAvatar(createdUser.id, createAvatarFile);
      }
      setCreateError("");
      setShowCreate(false);
      setCreateAvatarFile(null);
      setCreateForm({ username: "", email: "", firstName: "", lastName: "", phone: "", displayName: "", groupId: "", isPlatformAdmin: false, password: "" });
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      await queryClient.invalidateQueries({ queryKey: ["permission-groups"] });
    },
    onError: (err) => setCreateError(err instanceof Error ? err.message : "Anlage fehlgeschlagen")
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editRow) throw new Error("Kein Benutzer ausgewählt");
      const payload: Partial<{
        email: string;
        firstName: string;
        lastName: string;
        phone?: string;
        displayName: string;
        groupId: string | null;
        isPlatformAdmin: boolean;
        status: "aktiv" | "inaktiv";
        password: string;
      }> = {
        email: editForm.email.trim().toLowerCase(),
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        phone: editForm.phone.trim() || undefined,
        displayName: editForm.displayName.trim() || `${editForm.firstName.trim()} ${editForm.lastName.trim()}`.trim(),
        status: editForm.status,
        password: editForm.password.trim() || undefined
      };
      if (editRow.role !== "superadmin") {
        payload.groupId = editForm.groupId || null;
        payload.isPlatformAdmin = editForm.isPlatformAdmin;
      }
      return api.updateAdminUser(editRow.id, {
        ...payload
      });
    },
    onSuccess: async () => {
      if (!editRow) return;
      if (editAvatarFile) {
        await api.uploadAdminUserAvatar(editRow.id, editAvatarFile);
      } else if (editAvatarRemoved) {
        await api.deleteAdminUserAvatar(editRow.id);
      }
      setEditError("");
      setEditRow(null);
      setEditAvatarFile(null);
      setEditAvatarRemoved(false);
      setEditForm({ email: "", firstName: "", lastName: "", phone: "", displayName: "", groupId: "", isPlatformAdmin: false, status: "aktiv", password: "" });
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      await queryClient.invalidateQueries({ queryKey: ["permission-groups"] });
    },
    onError: (err) => setEditError(err instanceof Error ? err.message : "Speichern fehlgeschlagen")
  });

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => api.deleteAdminUser(userId),
    onSuccess: async (_, userId) => {
      setEditError("");
      setUserDeleteConfirm(null);
      if (editRow?.id === userId) setEditRow(null);
      await queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      await queryClient.invalidateQueries({ queryKey: ["permission-groups"] });
      await queryClient.invalidateQueries({ queryKey: ["object-scope"] });
    },
    onError: (err) => setEditError(err instanceof Error ? err.message : "Löschen fehlgeschlagen")
  });

  const columns = useMemo(
    () => [
      {
        key: "username",
        align: "center" as const,
        label: (
          <span className="table-head-two-line">
            <span>Benutzername</span>
            <span>&nbsp;</span>
          </span>
        )
      },
      {
        key: "avatar",
        label: (
          <span className="table-head-two-line">
            <span>&nbsp;</span>
            <span>&nbsp;</span>
          </span>
        ),
        align: "center" as const,
        width: 90,
        render: (r: AdminUserRow) =>
          r.avatarUrl ? (
            <img
              src={r.avatarUrl}
              alt={r.displayName}
              style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", border: "1px solid var(--line)" }}
            />
          ) : (
            <span
              title={r.displayName}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "1px solid var(--line)",
                background: "color-mix(in srgb, var(--surface-muted) 75%, #fff 25%)",
                color: "var(--text-soft)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                fontWeight: 700
              }}
            >
              {getUserInitials(r)}
            </span>
          )
      },
      {
        key: "displayName",
        align: "center" as const,
        label: (
          <span className="table-head-two-line">
            <span>Anzeigename</span>
            <span>&nbsp;</span>
          </span>
        )
      },
      {
        key: "group",
        align: "center" as const,
        label: (
          <span className="table-head-two-line">
            <span>Benutzergruppe</span>
            <span>&nbsp;</span>
          </span>
        ),
        render: (r: AdminUserRow) => {
          if (r.role === "superadmin") return <span className="badge">Superadmin (Sicherheitsrolle)</span>;
          if (r.role === "platform_admin") return <span className="badge">Platform Admin (Sicherheitsrolle)</span>;
          const assignment = effectiveGroupState.userAssignments[r.id];
          const groupId = assignment?.groupIds?.[0];
          const groupName = effectiveGroupState.groups.find((entry) => entry.id === groupId)?.name;
          return <span className="badge">{groupName || "Keine Gruppe"}</span>;
        }
      },
      {
        key: "status",
        label: (
          <span className="table-head-two-line">
            <span>Status</span>
            <span>&nbsp;</span>
          </span>
        ),
        align: "center" as const,
        render: (r: AdminUserRow) => <span className={`badge ${r.status === "aktiv" ? "aktiv" : "inaktiv"}`}>{r.status}</span>
      },
      {
        key: "lastLoginAt",
        align: "center" as const,
        label: (
          <span className="table-head-two-line">
            <span>Letzter Login</span>
            <span>&nbsp;</span>
          </span>
        ),
        render: (r: AdminUserRow) => (r.lastLoginAt ? new Date(r.lastLoginAt).toLocaleString("de-DE") : "-")
      },
      {
        key: "actions",
        label: (
          <span className="table-head-two-line">
            <span>Aktionen</span>
            <span>&nbsp;</span>
          </span>
        ),
        align: "center" as const,
        width: 54,
        render: (r: AdminUserRow) => {
          const menuId = `user:${r.id}`;
          return (
            <div className="resolutions-v2-action-wrap admin-users-action-wrap" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="resolutions-v2-action-trigger"
                aria-label={`Aktionen fuer ${r.displayName || r.username}`}
                aria-expanded={openActionMenuId === menuId}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (openActionMenuId === menuId) {
                    setOpenActionMenuId(null);
                    return;
                  }
                  openActionMenuAtTrigger(menuId, e.currentTarget);
                }}
              >
                &#8942;
              </button>
              {openActionMenuId === menuId && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                <div
                  className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                  style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseEnter={() => setIsActionMenuHovered(true)}
                  onMouseLeave={() => setIsActionMenuHovered(false)}
                >
                  <button
                    type="button"
                    disabled={!canUpdateUsers}
                    onClick={() => {
                      setEditError("");
                      setShowCreate(false);
                      setEditRow(r);
                      setEditAvatarFile(null);
                      setEditAvatarRemoved(false);
                      setEditForm({
                        email: r.email ?? "",
                        firstName: r.firstName ?? "",
                        lastName: r.lastName ?? "",
                        phone: r.phone ?? "",
                        displayName: r.displayName,
                        groupId: effectiveGroupState.userAssignments[r.id]?.groupIds?.[0] ?? "",
                        isPlatformAdmin: r.role === "platform_admin",
                        status: r.status,
                        password: ""
                      });
                      setOpenActionMenuId(null);
                    }}
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    disabled={!canDeleteUsers || deleteMutation.isPending || r.id === authUser.id}
                    onClick={() => {
                      setEditError("");
                      const label = r.displayName?.trim() || r.username;
                      setUserDeleteConfirm({
                        id: r.id,
                        label,
                        code: createSecurityCode(),
                        input: ["", "", "", ""],
                        error: ""
                      });
                      setOpenActionMenuId(null);
                    }}
                  >
                    {"L\u00f6schen"}
                  </button>
                </div>,
                document.body
              ) : null}
            </div>
          );
        }
      }
    ],
    [actionMenuPosition, authUser.id, canDeleteUsers, canUpdateUsers, deleteMutation.isPending, effectiveGroupState, openActionMenuId]
  );

  const groupColumns = useMemo(
    () => [
      {
        key: "name",
        label: (
          <span className="table-head-two-line">
            <span>Gruppenname</span>
            <span>&nbsp;</span>
          </span>
        )
      },
      {
        key: "description",
        label: (
          <span className="table-head-two-line">
            <span>Beschreibung</span>
            <span>&nbsp;</span>
          </span>
        ),
        render: (r: PermissionGroupRow) => r.description?.trim() || "-"
      },
      {
        key: "status",
        label: (
          <span className="table-head-two-line">
            <span>Status</span>
            <span>&nbsp;</span>
          </span>
        ),
        render: (r: PermissionGroupRow) => (
          <span className={`badge ${r.active ? "aktiv" : "inaktiv"}`}>{r.active ? "aktiv" : "inaktiv"}</span>
        )
      },
      {
        key: "actions",
        label: (
          <span className="table-head-two-line">
            <span>Aktionen</span>
            <span>&nbsp;</span>
          </span>
        ),
        align: "center" as const,
        width: 54,
        render: (r: PermissionGroupRow) => {
          const menuId = `group:${r.id}`;
          return (
            <div className="resolutions-v2-action-wrap admin-users-action-wrap" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="resolutions-v2-action-trigger"
                aria-label={`Aktionen fuer ${r.name}`}
                aria-expanded={openActionMenuId === menuId}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (openActionMenuId === menuId) {
                    setOpenActionMenuId(null);
                    return;
                  }
                  openActionMenuAtTrigger(menuId, e.currentTarget);
                }}
              >
                &#8942;
              </button>
              {openActionMenuId === menuId && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                <div
                  className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                  style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseEnter={() => setIsActionMenuHovered(true)}
                  onMouseLeave={() => setIsActionMenuHovered(false)}
                >
                  <button
                    type="button"
                    disabled={!canEditRoleMatrix || groupsMutation.isPending}
                    onClick={() => {
                      setSelectedGroupId(r.id);
                      setGroupEditId(r.id);
                      setGroupEditName(r.name);
                      setGroupEditDescription(r.description ?? "");
                      setGroupEditActive(r.active);
                      setGroupEditError("");
                      setOpenActionMenuId(null);
                    }}
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    disabled={!canEditRoleMatrix || groupsMutation.isPending}
                    onClick={() => {
                      setGroupDeleteConfirm({
                        id: r.id,
                        label: r.name,
                        code: createSecurityCode(),
                        input: ["", "", "", ""],
                        error: ""
                      });
                      setOpenActionMenuId(null);
                    }}
                  >
                    {"L\u00f6schen"}
                  </button>
                </div>,
                document.body
              ) : null}
            </div>
          );
        }
      }
    ],
    [actionMenuPosition, canEditRoleMatrix, groupsMutation.isPending, openActionMenuId]
  );

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
  const createEmail = createForm.email.trim().toLowerCase();
  const editEmail = editForm.email.trim().toLowerCase();
  const canCreate =
    createForm.username.trim() &&
    EMAIL_REGEX.test(createEmail) &&
    createForm.firstName.trim() &&
    createForm.lastName.trim() &&
    ((isPlatformRightsContext && createForm.isPlatformAdmin) || createForm.groupId) &&
    createForm.password.trim().length >= 8;
  const canEdit =
    !!editRow &&
    EMAIL_REGEX.test(editEmail) &&
    !!editForm.firstName.trim() &&
    !!editForm.lastName.trim();

  const permissionLabels: Record<RolePermissionKey, string> = {
    dashboard: "Dashboard",
    qr_scan: "QR Erfassung",
    resolutions: "Umlaufbeschlüsse",
    resolutions_create: "Umlaufbeschlüsse anlegen",
    resolutions_update: "Umlaufbeschlüsse bearbeiten",
    resolutions_delete: "Umlaufbeschlüsse löschen",
    resolutions_delete_completed: "Abgeschlossene Beschl\u00FCsse l\u00F6schen",
    objects: "Objekte",
    objects_create: "Objekte anlegen",
    objects_update: "Objekte bearbeiten",
    objects_delete: "Objekte löschen",
    units: "Einheiten",
    units_create: "Einheiten anlegen",
    units_update: "Einheiten bearbeiten",
    units_delete: "Einheiten löschen",
    units_view_technical: "Technische Einheiten (000) anzeigen",
    owners: "Eigentümer",
    owners_create: "Eigentümer anlegen",
    owners_update: "Eigentümer bearbeiten",
    owners_delete: "Eigentümer löschen",
    assignments: "Zuordnungen",
    assignments_create: "Zuordnungen anlegen",
    assignments_update: "Zuordnungen bearbeiten",
    assignments_delete: "Zuordnungen löschen",
    imports: "Importe",
    imports_create: "Importe starten",
    imports_update: "Importe bearbeiten",
    imports_delete: "Importe löschen",
    templates: "Vorlagen",
    templates_create: "Vorlagen anlegen",
    templates_update: "Vorlagen bearbeiten",
    templates_delete: "Vorlagen löschen",
    settings: "Einstellungen",
    settings_create: "Einstellungen anlegen",
    settings_update: "Einstellungen bearbeiten",
    settings_delete: "Einstellungen löschen",
    admin_users: "Benutzer & Rechte",
    admin_users_create: "Benutzer & Rechte anlegen",
    admin_users_update: "Benutzer & Rechte bearbeiten",
    admin_users_delete: "Benutzer & Rechte löschen",
    platform_tenants: "SaaS-Mandanten",
    platform_tenants_create: "SaaS-Mandanten anlegen",
    platform_tenants_update: "SaaS-Mandanten bearbeiten",
    platform_tenants_delete: "SaaS-Mandanten löschen",
    platform_services_notify: "Dienste: Störungsmeldung per Mail",
    audit: "Protokolle",
    debug: "Debug"
  };

  const permissionModules = useMemo(() => {
    const defs = [
      {
        id: "dashboard",
        label: "Dashboard",
        visibility: "dashboard" as RolePermissionKey,
        actions: [] as Array<{ id: "create" | "update" | "delete"; key: RolePermissionKey; label: string }>
      },
      {
        id: "resolutions",
        label: "Umlaufbeschlüsse",
        visibility: "resolutions" as RolePermissionKey,
        actions: [
          { id: "create", key: "resolutions_create" as RolePermissionKey, label: "Anlegen" },
          { id: "update", key: "resolutions_update" as RolePermissionKey, label: "Bearbeiten" },
          { id: "delete", key: "resolutions_delete" as RolePermissionKey, label: "Löschen" }
        ]
      },
      {
        id: "qr_scan",
        label: "QR Erfassung",
        visibility: "qr_scan" as RolePermissionKey,
        actions: [] as Array<{ id: "create" | "update" | "delete"; key: RolePermissionKey; label: string }>
      },
      {
        id: "resolutions_delete_completed",
        label: "Abgeschlossene Beschl\u00FCsse l\u00F6schen",
        visibility: "resolutions_delete_completed" as RolePermissionKey,
        actions: [] as Array<{ id: "create" | "update" | "delete"; key: RolePermissionKey; label: string }>
      },
      {
        id: "objects",
        label: "Objekte",
        visibility: "objects" as RolePermissionKey,
        actions: [
          { id: "create", key: "objects_create" as RolePermissionKey, label: "Anlegen" },
          { id: "update", key: "objects_update" as RolePermissionKey, label: "Bearbeiten" },
          { id: "delete", key: "objects_delete" as RolePermissionKey, label: "Löschen" }
        ]
      },
      {
        id: "units",
        label: "Einheiten",
        visibility: "units" as RolePermissionKey,
        actions: [
          { id: "create", key: "units_create" as RolePermissionKey, label: "Anlegen" },
          { id: "update", key: "units_update" as RolePermissionKey, label: "Bearbeiten" },
          { id: "delete", key: "units_delete" as RolePermissionKey, label: "Löschen" }
        ]
      },
      {
        id: "units_view_technical",
        label: "Technische Einheiten (000) anzeigen",
        visibility: "units_view_technical" as RolePermissionKey,
        actions: [] as Array<{ id: "create" | "update" | "delete"; key: RolePermissionKey; label: string }>
      },
      {
        id: "owners",
        label: "Eigentümer",
        visibility: "owners" as RolePermissionKey,
        actions: [
          { id: "create", key: "owners_create" as RolePermissionKey, label: "Anlegen" },
          { id: "update", key: "owners_update" as RolePermissionKey, label: "Bearbeiten" },
          { id: "delete", key: "owners_delete" as RolePermissionKey, label: "Löschen" }
        ]
      },
      {
        id: "assignments",
        label: "Zuordnungen",
        visibility: "assignments" as RolePermissionKey,
        actions: [
          { id: "create", key: "assignments_create" as RolePermissionKey, label: "Anlegen" },
          { id: "update", key: "assignments_update" as RolePermissionKey, label: "Bearbeiten" },
          { id: "delete", key: "assignments_delete" as RolePermissionKey, label: "Löschen" }
        ]
      },
      {
        id: "imports",
        label: "Importe",
        visibility: "imports" as RolePermissionKey,
        actions: [
          { id: "create", key: "imports_create" as RolePermissionKey, label: "Anlegen/Starten" },
          { id: "update", key: "imports_update" as RolePermissionKey, label: "Bearbeiten" },
          { id: "delete", key: "imports_delete" as RolePermissionKey, label: "Löschen" }
        ]
      },
      {
        id: "templates",
        label: "Vorlagen",
        visibility: "templates" as RolePermissionKey,
        actions: [
          { id: "create", key: "templates_create" as RolePermissionKey, label: "Anlegen" },
          { id: "update", key: "templates_update" as RolePermissionKey, label: "Bearbeiten" },
          { id: "delete", key: "templates_delete" as RolePermissionKey, label: "Löschen" }
        ]
      },
      {
        id: "settings",
        label: "Einstellungen",
        visibility: "settings" as RolePermissionKey,
        actions: [
          { id: "create", key: "settings_create" as RolePermissionKey, label: "Anlegen" },
          { id: "update", key: "settings_update" as RolePermissionKey, label: "Bearbeiten" },
          { id: "delete", key: "settings_delete" as RolePermissionKey, label: "Löschen" }
        ]
      },
      {
        id: "admin_users",
        label: "Benutzer & Rechte",
        visibility: "admin_users" as RolePermissionKey,
        actions: [
          { id: "create", key: "admin_users_create" as RolePermissionKey, label: "Anlegen" },
          { id: "update", key: "admin_users_update" as RolePermissionKey, label: "Bearbeiten" },
          { id: "delete", key: "admin_users_delete" as RolePermissionKey, label: "Löschen" }
        ]
      },
      {
        id: "platform_tenants",
        label: "SaaS-Mandanten",
        visibility: "platform_tenants" as RolePermissionKey,
        actions: [
          { id: "create", key: "platform_tenants_create" as RolePermissionKey, label: "Anlegen" },
          { id: "update", key: "platform_tenants_update" as RolePermissionKey, label: "Bearbeiten" },
          { id: "delete", key: "platform_tenants_delete" as RolePermissionKey, label: "Löschen" }
        ]
      },
      {
        id: "platform_services_notify",
        label: "Dienste-Benachrichtigung",
        visibility: "platform_services_notify" as RolePermissionKey,
        actions: [] as Array<{ id: "create" | "update" | "delete"; key: RolePermissionKey; label: string }>
      },
      {
        id: "audit",
        label: "Protokolle",
        visibility: "audit" as RolePermissionKey,
        actions: [] as Array<{ id: "create" | "update" | "delete"; key: RolePermissionKey; label: string }>
      },
      {
        id: "debug",
        label: "Debug",
        visibility: "debug" as RolePermissionKey,
        actions: [] as Array<{ id: "create" | "update" | "delete"; key: RolePermissionKey; label: string }>
      }
    ];
    const available = new Set(permissionKeys);
    return defs
      .filter((module) => available.has(module.visibility))
      .map((module) => ({
        ...module,
        actions: module.actions.filter((action) => available.has(action.key))
      }));
  }, [permissionKeys]);

  const ensureAssignment = (state: PermissionGroupStateResponse["state"], userId: string) => {
    const current = state.userAssignments[userId];
    if (current) return current;
    return {
      groupIds: [],
      overrides: Object.fromEntries(permissionKeys.map((key) => [key, "inherit"])) as Record<RolePermissionKey, PermissionOverrideValue>
    };
  };

  const users = data?.data ?? [];
  const availableGroupFilterOptions = useMemo(() => {
    const base = effectiveGroupState.groups
      .filter((group) => group.active)
      .map((group) => ({ id: group.id, name: group.name }));
    return [
      { id: "__superadmin__", name: "Superadmin (Sicherheitsrolle)" },
      { id: "__none__", name: "Keine Gruppe" },
      ...base
    ];
  }, [effectiveGroupState.groups]);
  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return users.filter((user) => {
      const assignment = effectiveGroupState.userAssignments[user.id];
      const groupId = assignment?.groupIds?.[0] ?? "";
      const groupName = effectiveGroupState.groups.find((entry) => entry.id === groupId)?.name ?? "";
      const matchesSearch =
        !q ||
        user.username.toLowerCase().includes(q) ||
        String(user.firstName ?? "").toLowerCase().includes(q) ||
        String(user.lastName ?? "").toLowerCase().includes(q) ||
        user.displayName.toLowerCase().includes(q) ||
        String(user.email ?? "").toLowerCase().includes(q) ||
        String(user.phone ?? "").toLowerCase().includes(q) ||
        groupName.toLowerCase().includes(q);
      if (!matchesSearch) return false;
      if (userStatusFilter && user.status !== userStatusFilter) return false;
      if (userGroupFilter) {
        if (userGroupFilter === "__superadmin__") return user.role === "superadmin";
        if (userGroupFilter === "__none__") return !groupId;
        return groupId === userGroupFilter;
      }
      return true;
    });
  }, [effectiveGroupState.groups, effectiveGroupState.userAssignments, userGroupFilter, userSearch, userStatusFilter, users]);
  const filteredGroups = useMemo(() => {
    const q = groupSearch.trim().toLowerCase();
    return effectiveGroupState.groups.filter((group) => {
      const matchesSearch =
        !q ||
        group.name.toLowerCase().includes(q) ||
        String(group.description ?? "").toLowerCase().includes(q);
      if (!matchesSearch) return false;
      if (groupStatusFilter) return groupStatusFilter === (group.active ? "aktiv" : "inaktiv");
      return true;
    });
  }, [effectiveGroupState.groups, groupSearch, groupStatusFilter]);
  const hasDuplicateGroupNames = useMemo(() => {
    const seen = new Set<string>();
    for (const group of effectiveGroupState.groups) {
      const key = group.name.trim().toLocaleLowerCase("de-DE");
      if (!key) continue;
      if (seen.has(key)) return true;
      seen.add(key);
    }
    return false;
  }, [effectiveGroupState.groups]);
  const filteredAssignmentUsers = useMemo(() => {
    const q = assignmentSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) => {
      const assignment = effectiveGroupState.userAssignments[user.id];
      const groupId = assignment?.groupIds?.[0] ?? "";
      const groupName = effectiveGroupState.groups.find((entry) => entry.id === groupId)?.name ?? "";
      return (
        user.username.toLowerCase().includes(q) ||
        String(user.firstName ?? "").toLowerCase().includes(q) ||
        String(user.lastName ?? "").toLowerCase().includes(q) ||
        user.displayName.toLowerCase().includes(q) ||
        String(user.email ?? "").toLowerCase().includes(q) ||
        String(user.phone ?? "").toLowerCase().includes(q) ||
        groupName.toLowerCase().includes(q)
      );
    });
  }, [assignmentSearch, effectiveGroupState.groups, effectiveGroupState.userAssignments, users]);
  const assignmentPageSize = 10;
  const assignmentTotalPages = Math.max(1, Math.ceil(filteredAssignmentUsers.length / assignmentPageSize));
  const assignmentCurrentPage = Math.min(assignmentPage, assignmentTotalPages);
  const pagedAssignmentUsers = useMemo(() => {
    const start = (assignmentCurrentPage - 1) * assignmentPageSize;
    return filteredAssignmentUsers.slice(start, start + assignmentPageSize);
  }, [assignmentCurrentPage, filteredAssignmentUsers]);
  const newGroupNameExists = useMemo(() => {
    const key = newGroupName.trim().toLocaleLowerCase("de-DE");
    if (!key) return false;
    return effectiveGroupState.groups.some((group) => group.name.trim().toLocaleLowerCase("de-DE") === key);
  }, [effectiveGroupState.groups, newGroupName]);
  const activeSelectedGroupId =
    selectedGroupId && effectiveGroupState.groups.some((group) => group.id === selectedGroupId)
      ? selectedGroupId
      : effectiveGroupState.groups[0]?.id ?? "";
  const selectedGroup = effectiveGroupState.groups.find((group) => group.id === activeSelectedGroupId) ?? null;
  const activeSelectedUserId =
    selectedUserId && users.some((row) => row.id === selectedUserId) ? selectedUserId : users[0]?.id ?? "";
  const selectedUserRow = users.find((row) => row.id === activeSelectedUserId);
  const selectedUserIsSystemRole = selectedUserRow?.role === "superadmin" || selectedUserRow?.role === "platform_admin";
  const selectedUserSystemRoleLabel = selectedUserRow?.role === "superadmin"
    ? "Superadmin (Sicherheitsrolle)"
    : selectedUserRow?.role === "platform_admin"
      ? "Platform Admin (Sicherheitsrolle)"
      : null;
  const selectedUserAssignment = activeSelectedUserId ? ensureAssignment(effectiveGroupState, activeSelectedUserId) : null;
  const activeGroupIdsForSelectedUser = selectedUserAssignment
    ? selectedUserAssignment.groupIds.filter((groupId) =>
        effectiveGroupState.groups.some((group) => group.id === groupId && group.active)
      )
    : [];
  const allObjects = (objectsData?.data ?? []) as ObjectRow[];
  const filteredScopeObjects = useMemo(() => {
    const q = scopeObjectSearch.trim().toLowerCase();
    if (!q) return allObjects;
    return allObjects.filter((object) => {
      const no = String(object.objektnummer ?? "").toLowerCase();
      const name = String(object.objektname ?? "").toLowerCase();
      return no.includes(q) || name.includes(q);
    });
  }, [allObjects, scopeObjectSearch]);
  const scopeObjectPageSize = 10;
  const scopeObjectTotalPages = Math.max(1, Math.ceil(filteredScopeObjects.length / scopeObjectPageSize));
  const scopeObjectCurrentPage = Math.min(scopeObjectPage, scopeObjectTotalPages);
  const pagedScopeObjects = useMemo(() => {
    const start = (scopeObjectCurrentPage - 1) * scopeObjectPageSize;
    return filteredScopeObjects.slice(start, start + scopeObjectPageSize);
  }, [filteredScopeObjects, scopeObjectCurrentPage]);
  const selectedUserScope =
    activeSelectedUserId && effectiveObjectScope.userAssignments[activeSelectedUserId]
      ? effectiveObjectScope.userAssignments[activeSelectedUserId]
      : { mode: "all" as const, objectIds: [] };

  return (
    <div className="admin-users-page">
      <section className={`page-header panel ${isFrameworkV2 ? "resolutions-v2-page-header" : "admin-users-page-header"}`}>
        <h2 className={isFrameworkV2 ? "resolutions-v2-title" : ""} style={{ margin: 0 }}>
          {isFrameworkV2 ? (
            <span className="resolutions-v2-title-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <rect x="5.2" y="4.8" width="13.6" height="14.4" rx="1.9" stroke="currentColor" strokeWidth="1.8" />
                <path d="M8.2 9.4h7.6M8.2 13h7.6M8.2 16.6h4.8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
          ) : null}
          {isFrameworkV2 ? (
            <span className="resolutions-v2-title-copy">
              <span className="resolutions-v2-title-main">Benutzer & Rechte</span>
              <span className="resolutions-v2-title-sub">Benutzer, Gruppen und Objektzugriffe verwalten</span>
            </span>
          ) : (
            <span>Benutzer & Rechte</span>
          )}
        </h2>
      </section>

      {isError ? <section className="panel error-text">{error instanceof Error ? error.message : "Fehler beim Laden"}</section> : null}

      <section className="panel admin-rights-guide">
        <h3 style={{ marginTop: 0, marginBottom: 8 }}>Rechteverwaltung in 3 Schritten</h3>
        <div className="admin-rights-guide-grid">
          <div className="admin-rights-guide-item">
            <div className="admin-rights-guide-step">
              <span className="admin-rights-guide-bullet">1</span>
            </div>
            <div className="admin-rights-guide-copy">
              <strong>Benutzeranlage</strong>
              <small>Benutzer anlegen, bearbeiten, aktivieren oder sperren.</small>
            </div>
          </div>
          <div className="admin-rights-guide-item">
            <div className="admin-rights-guide-step">
              <span className="admin-rights-guide-bullet">2</span>
            </div>
            <div className="admin-rights-guide-copy">
              <strong>Gruppenanlage | Zuordnung</strong>
              <small>Benutzergruppen definieren und Benutzer einer Gruppe zuordnen.</small>
            </div>
          </div>
          <div className="admin-rights-guide-item">
            <div className="admin-rights-guide-step">
              <span className="admin-rights-guide-bullet">3</span>
            </div>
            <div className="admin-rights-guide-copy">
              <strong>Einzelrechtevergabe | Objektzuweisung</strong>
              <small>Einzelrechte vergeben und Objektzugriff pro Benutzer steuern.</small>
            </div>
          </div>
        </div>
      </section>

      <section className="panel create-form admin-rights-userlist">
        <div className="page-header" style={{ marginBottom: 6 }}>
          <div className="admin-rights-step-like-header">
            <span className="admin-rights-guide-bullet">1</span>
            <h3 style={{ marginTop: 0, marginBottom: 0 }}>Benutzeranlage</h3>
          </div>
          <button
            className={isFrameworkV2 ? "resolutions-v2-create-btn" : ""}
            disabled={!canCreateUsers}
            onClick={() => setShowCreate((v) => !v)}
          >
            {isFrameworkV2 && !showCreate ? (
              <span className="resolutions-v2-create-btn-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none">
                  <path d="M10 4.2v11.6M4.2 10h11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
            ) : null}
            <span>{showCreate ? "Schließen" : "Neuer Benutzer"}</span>
          </button>
        </div>
        {showCreate ? (
          <section className="admin-rights-nested-box" style={{ marginBottom: 10 }}>
            <h3 style={{ marginTop: 0 }}>Benutzer anlegen</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>Benutzername*</label>
                <input value={createForm.username} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} />
              </div>
              <div className="form-field">
                <label>E-Mail*</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  placeholder="name@beispiel.de"
                  required
                />
              </div>
              <div className="form-field">
                <label>Vorname*</label>
                <input value={createForm.firstName} onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })} required />
              </div>
              <div className="form-field">
                <label>Nachname*</label>
                <input value={createForm.lastName} onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })} required />
              </div>
              <div className="form-field">
                <label>Telefon</label>
                <input value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Anzeigename (optional)</label>
                <input value={createForm.displayName} onChange={(e) => setCreateForm({ ...createForm, displayName: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Gruppe*</label>
                <select
                  value={createForm.groupId}
                  disabled={isPlatformRightsContext && createForm.isPlatformAdmin}
                  onChange={(e) => setCreateForm({ ...createForm, groupId: e.target.value })}
                >
                  <option value="">Bitte Gruppe wählen</option>
                  {effectiveGroupState.groups
                    .filter((group) => group.active)
                    .map((group) => (
                      <option key={`create-group-${group.id}`} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                </select>
              </div>
              {isPlatformRightsContext ? (
                <div className="form-field">
                  <label>Systemrolle</label>
                  <label className="admin-rights-inline-check">
                    <input
                      type="checkbox"
                      checked={createForm.isPlatformAdmin}
                      disabled={authUser.role !== "superadmin"}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          isPlatformAdmin: e.target.checked,
                          groupId: e.target.checked ? "" : createForm.groupId
                        })
                      }
                    />
                    <span>Als Platform Admin anlegen</span>
                  </label>
                </div>
              ) : null}
              <div className="form-field">
                <label>Initialpasswort* (min. 8 Zeichen)</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label>Profilbild</label>
                <input
                  ref={createAvatarInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => setCreateAvatarFile(normalizeAvatarFile(e.target.files?.[0] ?? null))}
                />
                <div
                  className={`resolution-document-dropzone${createAvatarDragOver ? " is-drag-over" : ""}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setCreateAvatarDragOver(true);
                  }}
                  onDragLeave={() => setCreateAvatarDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setCreateAvatarDragOver(false);
                    const file = normalizeAvatarFile(e.dataTransfer.files?.[0] ?? null);
                    if (file) setCreateAvatarFile(file);
                  }}
                >
                  <div style={{ display: "grid", gap: 8, justifyItems: "center", width: "100%" }}>
                    <button type="button" onClick={() => createAvatarInputRef.current?.click()}>
                      Datei auswählen
                    </button>
                    <small className="resolution-document-dropzone-text">
                      Drag & Drop oder Klick zum Auswählen
                    </small>
                    <small style={{ color: "var(--text-soft)" }}>
                      {createAvatarFile ? `Ausgewählt: ${createAvatarFile.name}` : "Keine Datei ausgewählt"}
                    </small>
                  </div>
                </div>
              </div>
            </div>
            <div className="form-actions">
              <button disabled={!canCreateUsers || !canCreate || createMutation.isPending} onClick={() => createMutation.mutate()}>
                {createMutation.isPending ? "Speichere..." : "Benutzer speichern"}
              </button>
              {createError ? <span className="error-text">{createError}</span> : null}
            </div>
          </section>
        ) : null}
        {editRow ? (
          <section className="admin-rights-nested-box" style={{ marginBottom: 10 }}>
            <h3 style={{ marginTop: 0 }}>Benutzer bearbeiten</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>Benutzername</label>
                <input value={editRow.username} disabled />
              </div>
              <div className="form-field">
                <label>E-Mail*</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required />
              </div>
              <div className="form-field">
                <label>Vorname*</label>
                <input value={editForm.firstName} onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })} required />
              </div>
              <div className="form-field">
                <label>Nachname*</label>
                <input value={editForm.lastName} onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })} required />
              </div>
              <div className="form-field">
                <label>Telefon</label>
                <input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Anzeigename (optional)</label>
                <input value={editForm.displayName} onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Gruppe</label>
                {editRow.role === "superadmin" ? (
                  <input value="Superadmin (Sicherheitsrolle)" disabled />
                ) : (
                  <select
                    value={editForm.groupId}
                    disabled={isPlatformRightsContext && editForm.isPlatformAdmin}
                    onChange={(e) => setEditForm({ ...editForm, groupId: e.target.value })}
                  >
                    <option value="">Keine Gruppe</option>
                    {effectiveGroupState.groups
                      .filter((group) => group.active)
                      .map((group) => (
                        <option key={`edit-group-${group.id}`} value={group.id}>
                          {group.name}
                        </option>
                      ))}
                  </select>
                )}
              </div>
              {isPlatformRightsContext && editRow.role !== "superadmin" ? (
                <div className="form-field">
                  <label>Systemrolle</label>
                  <label className="admin-rights-inline-check">
                    <input
                      type="checkbox"
                      checked={editForm.isPlatformAdmin}
                      disabled={authUser.role !== "superadmin"}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          isPlatformAdmin: e.target.checked,
                          groupId: e.target.checked ? "" : editForm.groupId
                        })
                      }
                    />
                    <span>Platform Admin</span>
                  </label>
                </div>
              ) : null}
              <div className="form-field">
                <label>Status*</label>
            <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value as "aktiv" | "inaktiv" })}>
              <option value="aktiv">aktiv</option>
              <option value="inaktiv">inaktiv</option>
            </select>
              </div>
              <div className="form-field">
                <label>Neues Passwort (optional)</label>
                <input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Profilbild</label>
                {editRow?.avatarUrl && !editAvatarRemoved && !editAvatarFile ? (
                  <div style={{ marginBottom: 8 }}>
                    <img
                      src={editRow.avatarUrl}
                      alt={editRow.displayName}
                      style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", border: "1px solid var(--line)" }}
                    />
                  </div>
                ) : null}
                <input
                  ref={editAvatarInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    setEditAvatarFile(normalizeAvatarFile(e.target.files?.[0] ?? null));
                    setEditAvatarRemoved(false);
                  }}
                />
                <div
                  className={`resolution-document-dropzone${editAvatarDragOver ? " is-drag-over" : ""}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setEditAvatarDragOver(true);
                  }}
                  onDragLeave={() => setEditAvatarDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setEditAvatarDragOver(false);
                    const file = normalizeAvatarFile(e.dataTransfer.files?.[0] ?? null);
                    if (!file) return;
                    setEditAvatarFile(file);
                    setEditAvatarRemoved(false);
                  }}
                >
                  <div style={{ display: "grid", gap: 8, justifyItems: "center", width: "100%" }}>
                    <button type="button" onClick={() => editAvatarInputRef.current?.click()}>
                      Datei auswählen
                    </button>
                    <small className="resolution-document-dropzone-text">
                      Drag & Drop oder Klick zum Auswählen
                    </small>
                    <small style={{ color: "var(--text-soft)" }}>
                      {editAvatarFile
                        ? `Ausgewählt: ${editAvatarFile.name}`
                        : editAvatarRemoved
                          ? "Bild wird beim Speichern entfernt"
                          : "Keine neue Datei ausgewählt"}
                    </small>
                  </div>
                </div>
                {editRow?.avatarUrl ? (
                  <button
                    type="button"
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      setEditAvatarFile(null);
                      setEditAvatarRemoved(true);
                    }}
                  >
                    Bild entfernen
                  </button>
                ) : null}
              </div>
            </div>
            <div className="form-actions">
              <button disabled={!canUpdateUsers || !canEdit || updateMutation.isPending} onClick={() => updateMutation.mutate()}>
                {updateMutation.isPending ? "Speichere..." : "Änderungen speichern"}
              </button>
              <button onClick={() => setEditRow(null)}>Abbrechen</button>
              {editError ? <span className="error-text">{editError}</span> : null}
            </div>
          </section>
        ) : null}
        <small style={{ color: "var(--text-soft)" }}>Benutzer anlegen, bearbeiten, aktivieren oder sperren.</small>
        <div className="admin-users-v2-table" style={{ marginTop: 10 }}>
      <ServerDataTable
        columns={columns}
        rows={filteredUsers}
        total={filteredUsers.length}
        page={1}
        pageSize={50}
        onPageChange={() => undefined}
        topContent={
          <div className="filterbar">
            <SearchField
              placeholder="Suche Benutzer/Name/Gruppe"
              value={userSearch}
              onChange={setUserSearch}
            />
            <select value={userGroupFilter} onChange={(e) => setUserGroupFilter(e.target.value)}>
              <option value="">Alle Gruppen</option>
              {availableGroupFilterOptions.map((entry) => (
                <option key={`user-group-filter-${entry.id}`} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
            <select value={userStatusFilter} onChange={(e) => setUserStatusFilter(e.target.value as "" | "aktiv" | "inaktiv")}>
              <option value="">Alle Status</option>
              <option value="aktiv">aktiv</option>
              <option value="inaktiv">inaktiv</option>
            </select>
          </div>
        }
      />
        </div>
      </section>

      {isLoading ? <section className="panel">Lade Benutzer...</section> : null}

      <section className="panel create-form admin-rights-section-card">
        <div className="admin-rights-step-like-header">
          <span className="admin-rights-guide-bullet">2</span>
          <h3 style={{ marginTop: 0, marginBottom: 0 }}>Gruppenanlage | Zuordnung</h3>
        </div>
        <small style={{ color: "var(--text-soft)" }}>
          Benutzergruppen definieren, Rechte je Gruppe pflegen und Benutzer einer Gruppe zuordnen.
        </small>
        {groupsLoading ? (
          <p>Lade Usergruppen...</p>
        ) : (
          <>
            <section className="admin-permission-group-card" style={{ marginTop: 12 }}>
              <div className="page-header" style={{ marginBottom: 6 }}>
                <div className="admin-permission-group-title">Benutzergruppen</div>
                <button
                  type="button"
                  className={isFrameworkV2 ? "resolutions-v2-create-btn" : ""}
                  onClick={() => setShowGroupCreate((prev) => !prev)}
                >
                  {isFrameworkV2 && !showGroupCreate ? (
                    <span className="resolutions-v2-create-btn-icon" aria-hidden="true">
                      <svg viewBox="0 0 20 20" fill="none">
                        <path d="M10 4.2v11.6M4.2 10h11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    </span>
                  ) : null}
                  <span>{showGroupCreate ? "Schließen" : "Gruppenanlage"}</span>
                </button>
              </div>
              <small style={{ color: "var(--text-soft)" }}>
                Übersicht aller Gruppen mit Gruppenname, Beschreibung und Status.
              </small>
              {showGroupCreate ? (
                <div className="admin-rights-nested-subbox" style={{ marginTop: 10 }}>
                  <div className="form-grid" style={{ marginTop: 0 }}>
                    <div className="form-field">
                      <label>Gruppenname*</label>
                      <input
                        placeholder="Neue Benutzergruppe"
                        value={newGroupName}
                        disabled={!canEditRoleMatrix || groupsMutation.isPending}
                        onChange={(e) => setNewGroupName(e.target.value)}
                      />
                    </div>
                    <div className="form-field">
                      <label>Beschreibung (optional)</label>
                      <input
                        placeholder="Kurze Beschreibung der Gruppe"
                        value={newGroupDescription}
                        disabled={!canEditRoleMatrix || groupsMutation.isPending}
                        onChange={(e) => setNewGroupDescription(e.target.value)}
                      />
                    </div>
                    <div className="form-field">
                      <label>Status</label>
                      <label className="settings-switch-row admin-rights-group-switch-small" style={{ margin: "0" }}>
                        <span className="settings-toggle admin-rights-group-toggle-small">
                          <input
                            type="checkbox"
                            checked={newGroupActive}
                            disabled={!canEditRoleMatrix || groupsMutation.isPending}
                            onChange={(e) => setNewGroupActive(e.target.checked)}
                          />
                          <span className="settings-toggle-slider" />
                        </span>
                        <span style={{ fontSize: 12, color: "var(--text-soft)" }}>{newGroupActive ? "aktiv" : "inaktiv"}</span>
                      </label>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      disabled={!canEditRoleMatrix || groupsMutation.isPending || !newGroupName.trim() || newGroupNameExists}
                      onClick={() => {
                        const newKey = newGroupName.trim().toLocaleLowerCase("de-DE");
                        const exists = effectiveGroupState.groups.some((group) => group.name.trim().toLocaleLowerCase("de-DE") === newKey);
                        if (exists) {
                          setGroupsMessage("");
                          setGroupsError("Benutzergruppenname bereits vorhanden.");
                          return;
                        }
                        const basePermissions = Object.fromEntries(permissionKeys.map((key) => [key, false])) as Record<
                          RolePermissionKey,
                          boolean
                        >;
                        const nextGroup: PermissionGroupRow = {
                          id: `grp_${Date.now().toString(36)}`,
                          name: newGroupName.trim(),
                          description: newGroupDescription.trim() || undefined,
                          active: newGroupActive,
                          permissions: basePermissions
                        };
                        setGroupsMessage("");
                        setGroupsError("");
                        setGroupsDraft({
                          ...effectiveGroupState,
                          groups: [...effectiveGroupState.groups, nextGroup]
                        });
                        setSelectedGroupId(nextGroup.id);
                        setNewGroupName("");
                        setNewGroupDescription("");
                        setNewGroupActive(true);
                        setShowGroupCreate(false);
                      }}
                    >
                      Anlegen
                    </button>
                  </div>
                  {newGroupNameExists ? <div className="error-text">Benutzergruppenname bereits vorhanden.</div> : null}
                </div>
              ) : null}
              <div className="admin-users-v2-table" style={{ marginTop: 8 }}>
                <ServerDataTable
                  columns={groupColumns}
                  rows={filteredGroups}
                  total={filteredGroups.length}
                  page={1}
                  pageSize={50}
                  onPageChange={() => undefined}
                  topContent={
                    <div className="filterbar">
                      <SearchField
                        placeholder="Suche Gruppenname/Beschreibung"
                        value={groupSearch}
                        onChange={setGroupSearch}
                      />
                      <select
                        value={groupStatusFilter}
                        onChange={(e) => setGroupStatusFilter(e.target.value as "" | "aktiv" | "inaktiv")}
                      >
                        <option value="">Alle Status</option>
                        <option value="aktiv">aktiv</option>
                        <option value="inaktiv">inaktiv</option>
                      </select>
                    </div>
                  }
                />
              </div>
              {groupEditId ? (
                <div className="admin-rights-nested-subbox" style={{ marginTop: 10 }}>
                  <div className="admin-permission-group-title" style={{ fontSize: 16 }}>Gruppe bearbeiten</div>
                  <div className="form-grid" style={{ marginTop: 8 }}>
                    <div className="form-field">
                      <label>Gruppenname*</label>
                      <input
                        value={groupEditName}
                        disabled={!canEditRoleMatrix || groupsMutation.isPending}
                        onChange={(e) => {
                          setGroupEditName(e.target.value);
                          setGroupEditError("");
                        }}
                      />
                    </div>
                    <div className="form-field">
                      <label>Beschreibung (optional)</label>
                      <input
                        value={groupEditDescription}
                        disabled={!canEditRoleMatrix || groupsMutation.isPending}
                        onChange={(e) => setGroupEditDescription(e.target.value)}
                      />
                    </div>
                    <div className="form-field">
                      <label>Status</label>
                      <label className="settings-switch-row admin-rights-group-switch-small" style={{ margin: "0" }}>
                        <span className="settings-toggle admin-rights-group-toggle-small">
                          <input
                            type="checkbox"
                            checked={groupEditActive}
                            disabled={!canEditRoleMatrix || groupsMutation.isPending}
                            onChange={(e) => setGroupEditActive(e.target.checked)}
                          />
                          <span className="settings-toggle-slider" />
                        </span>
                        <span style={{ fontSize: 12, color: "var(--text-soft)" }}>{groupEditActive ? "aktiv" : "inaktiv"}</span>
                      </label>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      disabled={!canEditRoleMatrix || groupsMutation.isPending || !groupEditName.trim()}
                      onClick={() => {
                        const key = groupEditName.trim().toLocaleLowerCase("de-DE");
                        const duplicate = effectiveGroupState.groups.some(
                          (group) => group.id !== groupEditId && group.name.trim().toLocaleLowerCase("de-DE") === key
                        );
                        if (duplicate) {
                          setGroupEditError("Benutzergruppenname bereits vorhanden.");
                          return;
                        }
                        const nextGroups = effectiveGroupState.groups.map((group) =>
                          group.id === groupEditId
                            ? {
                                ...group,
                                name: groupEditName.trim(),
                                description: groupEditDescription.trim() || undefined,
                                active: groupEditActive
                              }
                            : group
                        );
                        setGroupsMessage("");
                        setGroupsError("");
                        setGroupsDraft({ ...effectiveGroupState, groups: nextGroups });
                        setGroupEditId("");
                        setGroupEditName("");
                        setGroupEditDescription("");
                        setGroupEditActive(true);
                        setGroupEditError("");
                      }}
                    >
                      Gruppe übernehmen
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setGroupEditId("");
                        setGroupEditName("");
                        setGroupEditDescription("");
                        setGroupEditActive(true);
                        setGroupEditError("");
                      }}
                    >
                      Abbrechen
                    </button>
                    {groupEditError ? <span className="error-text">{groupEditError}</span> : null}
                  </div>
                </div>
              ) : null}
            </section>

            {selectedGroup ? (
              <section className="admin-permission-group-card" style={{ marginTop: 12 }}>
                <div className="admin-permission-group-title">Rechtezuordnung zur Gruppe</div>
                <small style={{ color: "var(--text-soft)" }}>
                  Rechte der ausgewählten Gruppe konfigurieren.
                </small>
                <div className="admin-rights-nested-subbox" style={{ marginTop: 10 }}>
                  <div className="admin-group-rights-select-row">
                    <div className="admin-group-rights-select-field">
                      <label className="field-label" htmlFor="group-rights-select">
                        Benutzergruppe auswählen
                      </label>
                      <select
                        id="group-rights-select"
                        value={activeSelectedGroupId}
                        disabled={groupsMutation.isPending}
                        onChange={(e) => {
                          setSelectedGroupId(e.target.value);
                        }}
                      >
                        {effectiveGroupState.groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="admin-permission-table-wrap">
                    <table className="data-table admin-rights-table">
                      <thead>
                        <tr>
                          <th>
                            <span className="table-head-two-line">
                              <span>Bereich</span>
                              <span>&nbsp;</span>
                            </span>
                          </th>
                          <th style={{ textAlign: "center" }}>
                            <span className="table-head-two-line">
                              <span>Sichtbar</span>
                              <span>&nbsp;</span>
                            </span>
                          </th>
                          <th style={{ textAlign: "center" }}>
                            <span className="table-head-two-line">
                              <span>Anlegen</span>
                              <span>&nbsp;</span>
                            </span>
                          </th>
                          <th style={{ textAlign: "center" }}>
                            <span className="table-head-two-line">
                              <span>Bearbeiten</span>
                              <span>&nbsp;</span>
                            </span>
                          </th>
                          <th style={{ textAlign: "center" }}>
                            <span className="table-head-two-line">
                              <span>Löschen</span>
                              <span>&nbsp;</span>
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {permissionModules.map((module) => (
                          <tr key={`group-module-${selectedGroup.id}-${module.id}`}>
                            <td>{module.label}</td>
                            <td style={{ textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={Boolean(selectedGroup.permissions[module.visibility])}
                                disabled={!canEditRoleMatrix || groupsMutation.isPending}
                                onChange={(e) => {
                                  const nextGroups = effectiveGroupState.groups.map((entry) =>
                                    entry.id === selectedGroup.id
                                      ? {
                                          ...entry,
                                          permissions: {
                                            ...entry.permissions,
                                            [module.visibility]: e.target.checked
                                          }
                                        }
                                      : entry
                                  );
                                  setGroupsMessage("");
                                  setGroupsError("");
                                  setGroupsDraft({ ...effectiveGroupState, groups: nextGroups });
                                }}
                              />
                            </td>
                            {(["create", "update", "delete"] as const).map((actionId) => {
                              const action = module.actions.find((entry) => entry.id === actionId);
                              if (!action) {
                                return (
                                  <td key={`group-module-${selectedGroup.id}-${module.id}-${actionId}`} style={{ textAlign: "center" }}>
                                    -
                                  </td>
                                );
                              }
                              return (
                                <td key={`group-module-${selectedGroup.id}-${module.id}-${actionId}`} style={{ textAlign: "center" }}>
                                  <input
                                    type="checkbox"
                                    checked={Boolean(selectedGroup.permissions[action.key])}
                                    disabled={!canEditRoleMatrix || groupsMutation.isPending}
                                    onChange={(e) => {
                                      const nextGroups = effectiveGroupState.groups.map((entry) =>
                                        entry.id === selectedGroup.id
                                          ? {
                                              ...entry,
                                              permissions: {
                                                ...entry.permissions,
                                                [action.key]: e.target.checked
                                              }
                                            }
                                          : entry
                                      );
                                      setGroupsMessage("");
                                      setGroupsError("");
                                      setGroupsDraft({ ...effectiveGroupState, groups: nextGroups });
                                    }}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="form-actions">
                    <button
                      type="button"
                      disabled={!canEditRoleMatrix || !groupsDraft || groupsMutation.isPending}
                      onClick={() => {
                        if (!groupsDraft) return;
                        if (hasDuplicateGroupNames) {
                          setGroupsMessage("");
                          setGroupsError("Es darf keine doppelten Benutzergruppennamen geben.");
                          return;
                        }
                        groupsMutation.mutate(groupsDraft);
                      }}
                    >
                      {groupsMutation.isPending ? "Speichere..." : "Benutzergruppe speichern"}
                    </button>
                    {groupsError ? <span className="error-text">{groupsError}</span> : null}
                    {groupsMessage ? <span>{groupsMessage}</span> : null}
                  </div>
                </div>
              </section>
            ) : (
              <div className="admin-rights-user-override-note" style={{ marginTop: 8 }}>
                Es gibt noch keine Gruppen. Bitte zuerst eine neue Benutzergruppe anlegen oder in der Gruppenliste auswählen.
              </div>
            )}

            <section className="admin-permission-group-card" style={{ marginTop: 12 }}>
              <div className="admin-permission-group-title">Benutzer-Gruppenzuordnung</div>
              <small style={{ color: "var(--text-soft)" }}>
                Hier wird direkt festgelegt, welche Benutzer zu welcher Gruppe gehören.
              </small>
              <div className="filterbar" style={{ marginTop: 8 }}>
                <SearchField
                  placeholder="Suche Benutzer/Gruppe"
                  value={assignmentSearch}
                  onChange={(value) => {
                    setAssignmentSearch(value);
                    setAssignmentPage(1);
                  }}
                />
              </div>
              <small style={{ color: "var(--text-soft)" }}>
                Zeilen: {pagedAssignmentUsers.length} / Gesamt: {filteredAssignmentUsers.length}
              </small>
              {!effectiveGroupState.groups.length ? (
                <div className="admin-rights-user-override-note" style={{ marginTop: 8 }}>
                  Es gibt noch keine Gruppen. Bitte zuerst oben eine Gruppe anlegen.
                </div>
              ) : (
                <div className="admin-permission-table-wrap" style={{ marginTop: 8 }}>
                  <table className="data-table admin-rights-table">
                    <thead>
                      <tr>
                        <th>Benutzer</th>
                        <th style={{ textAlign: "center", whiteSpace: "nowrap" }}>Keine Gruppe</th>
                        {effectiveGroupState.groups.map((group) => (
                          <th key={`assign-head-${group.id}`} style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                            {group.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {pagedAssignmentUsers.map((user) => {
                        const assignment = ensureAssignment(effectiveGroupState, user.id);
                        return (
                          <tr key={`assign-row-${user.id}`}>
                            <td>
                              <strong>{user.displayName}</strong>
                              <div style={{ fontSize: 12, color: "var(--text-soft)" }}>{user.username}</div>
                            </td>
                            <td style={{ textAlign: "center" }}>
                              <input
                                type="radio"
                                name={`user-group-${user.id}`}
                                checked={!assignment.groupIds[0]}
                                disabled={
                                  !canEditRoleMatrix ||
                                  groupsMutation.isPending ||
                                  user.role === "superadmin" ||
                                  user.role === "platform_admin"
                                }
                                onChange={() => {
                                  const current = ensureAssignment(effectiveGroupState, user.id);
                                  setGroupsMessage("");
                                  setGroupsError("");
                                  setGroupsDraft({
                                    ...effectiveGroupState,
                                    userAssignments: {
                                      ...effectiveGroupState.userAssignments,
                                      [user.id]: {
                                        ...current,
                                        groupIds: []
                                      }
                                    }
                                  });
                                }}
                              />
                            </td>
                            {effectiveGroupState.groups.map((group) => {
                              const checked = assignment.groupIds[0] === group.id;
                              return (
                                <td key={`assign-cell-${user.id}-${group.id}`} style={{ textAlign: "center" }}>
                                  <input
                                    type="radio"
                                    name={`user-group-${user.id}`}
                                    checked={checked}
                                    disabled={
                                      !canEditRoleMatrix ||
                                      groupsMutation.isPending ||
                                      user.role === "superadmin" ||
                                      user.role === "platform_admin"
                                    }
                                    onChange={() => {
                                      const current = ensureAssignment(effectiveGroupState, user.id);
                                      const nextGroupIds = [group.id];
                                      setGroupsMessage("");
                                      setGroupsError("");
                                      setGroupsDraft({
                                        ...effectiveGroupState,
                                        userAssignments: {
                                          ...effectiveGroupState.userAssignments,
                                          [user.id]: {
                                            ...current,
                                            groupIds: nextGroupIds
                                          }
                                        }
                                      });
                                    }}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                      {!pagedAssignmentUsers.length ? (
                        <tr>
                          <td colSpan={2 + effectiveGroupState.groups.length}>Keine Benutzer gefunden.</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              )}
              <div
                className="pagination"
                style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}
              >
                <button type="button" disabled={assignmentCurrentPage <= 1} onClick={() => setAssignmentPage((p) => Math.max(1, p - 1))}>
                  Zurück
                </button>
                <span style={{ fontSize: 13 }}>
                  Seite {assignmentCurrentPage} / {assignmentTotalPages}
                </span>
                <button
                  type="button"
                  disabled={assignmentCurrentPage >= assignmentTotalPages}
                  onClick={() => setAssignmentPage((p) => Math.min(assignmentTotalPages, p + 1))}
                >
                  Weiter
                </button>
              </div>
            </section>

          </>
        )}
      </section>

      <section className="panel create-form admin-rights-section-card">
        <div className="admin-rights-step-like-header">
          <span className="admin-rights-guide-bullet">3</span>
          <h3 style={{ marginTop: 0, marginBottom: 0 }}>Einzelrechtevergabe | Objektzuweisung</h3>
        </div>
        <small style={{ color: "var(--text-soft)" }}>
          Einzelrechte vergeben und Objektzugriff pro Benutzer steuern.
        </small>
        {activeSelectedUserId && selectedUserAssignment ? (
          <div className="admin-rights-nested-box">
            <div className="admin-rights-section-intro">
              <h4 style={{ margin: 0 }}>Einzelrechte pro Benutzer</h4>
              <small>
                In diesem Bereich werden benutzerbezogene Einzelrechte und die Objektzuordnung gepflegt. Gruppenrechte bleiben führend.
              </small>
            </div>
            <div className="form-grid" style={{ marginTop: 0 }}>
              <div className="form-field">
                <label>Benutzer auswählen (Einzelrechte)</label>
                <select value={activeSelectedUserId} onChange={(e) => setSelectedUserId(e.target.value)} disabled={!users.length}>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {`${user.displayName} (${user.username})`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="admin-rights-nested-subbox admin-group-assignment-info">
              <div style={{ marginBottom: 8, fontWeight: 600 }}>Gruppenzuweisung (informativ)</div>
              <div className="admin-group-assignment-info-row">
                {selectedUserSystemRoleLabel ? (
                  <label style={{ display: "flex", alignItems: "center", gap: 6, opacity: 0.85, cursor: "not-allowed" }}>
                    <input type="radio" checked={true} disabled />
                    <span className="badge">{selectedUserSystemRoleLabel}</span>
                  </label>
                ) : null}
                {effectiveGroupState.groups.map((group) => {
                  const checked = selectedUserAssignment.groupIds[0] === group.id;
                  return (
                    <label key={`assign-${group.id}`} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input
                        type="radio"
                        name={`selected-user-group-${activeSelectedUserId}`}
                        checked={checked}
                        disabled
                      />
                      <span className="badge">{group.name}</span>
                    </label>
                  );
                })}
                <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input
                    type="radio"
                    name={`selected-user-group-${activeSelectedUserId}`}
                    checked={!selectedUserAssignment.groupIds[0]}
                    disabled
                  />
                  <span className="badge">Keine Gruppe</span>
                </label>
              </div>
              <div style={{ marginTop: 8, color: "var(--text-soft)", fontSize: 12 }}>
                Die Gruppenzuordnung wird im Bereich „2 Gruppenanlage | Zuordnung“ gepflegt.
              </div>
            </div>

            {selectedUserIsSystemRole ? (
              <div className="admin-rights-user-override-note">
                Für Systemrollen (Superadmin / Platform Admin) sind Gruppen und Einzelrechte nicht bearbeitbar.
              </div>
            ) : activeGroupIdsForSelectedUser.length ? (
              <div className="admin-rights-user-override-note">Gruppe zugewiesen: Benutzer-Einzelrechte sind ausgeblendet.</div>
            ) : (
              <div className="admin-rights-user-override-note">Keine Gruppe zugewiesen. Benutzer-Einzelrechte greifen direkt.</div>
            )}
            {!selectedUserIsSystemRole && !activeGroupIdsForSelectedUser.length ? (
              <div className="admin-rights-nested-subbox">
                <div style={{ marginTop: 4, marginBottom: 8, fontWeight: 600 }}>Benutzer-Einzelrechte (nachrangig)</div>
                <div style={{ overflowX: "auto" }}>
                  <table className="data-table admin-rights-table">
                    <thead>
                      <tr>
                        <th>Bereich</th>
                        <th style={{ textAlign: "center" }}>Sichtbar</th>
                        <th style={{ textAlign: "center" }}>Anlegen</th>
                        <th style={{ textAlign: "center" }}>Bearbeiten</th>
                        <th style={{ textAlign: "center" }}>Löschen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {permissionModules.map((module) => (
                        <tr key={`override-module-${module.id}`}>
                          <td>{module.label}</td>
                          {[module.visibility, ...module.actions.map((action) => action.key)].map((key, index) => (
                            <td key={`override-module-${module.id}-${key}`} style={{ textAlign: "center" }}>
                              {index > 0 && !module.actions[index - 1] ? (
                                "-"
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={selectedUserAssignment.overrides[key] === "allow"}
                                  disabled={!canEditRoleMatrix || groupsMutation.isPending}
                                  onChange={(e) => {
                                    const current = ensureAssignment(effectiveGroupState, activeSelectedUserId);
                                    setGroupsMessage("");
                                    setGroupsError("");
                                    setGroupsDraft({
                                      ...effectiveGroupState,
                                      userAssignments: {
                                        ...effectiveGroupState.userAssignments,
                                        [activeSelectedUserId]: {
                                          ...current,
                                          overrides: {
                                            ...current.overrides,
                                            [key]: e.target.checked ? "allow" : "inherit"
                                          }
                                        }
                                      }
                                    });
                                  }}
                                />
                              )}
                            </td>
                          ))}
                          {module.actions.length < 3
                            ? Array.from({ length: 3 - module.actions.length }).map((_, idx) => (
                                <td key={`override-module-pad-${module.id}-${idx}`} style={{ textAlign: "center" }}>
                                  -
                                </td>
                              ))
                            : null}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="form-actions">
                  <button
                    type="button"
                    disabled={!canEditRoleMatrix || !groupsDraft || groupsMutation.isPending}
                    onClick={() => {
                      if (!groupsDraft) return;
                      if (hasDuplicateGroupNames) {
                        setGroupsMessage("");
                        setGroupsError("Es darf keine doppelten Benutzergruppennamen geben.");
                        return;
                      }
                      groupsMutation.mutate(groupsDraft);
                    }}
                  >
                    {groupsMutation.isPending ? "Speichere..." : "Einzelrechte speichern"}
                  </button>
                  {groupsError ? <span className="error-text">{groupsError}</span> : null}
                  {groupsMessage ? <span>{groupsMessage}</span> : null}
                </div>
              </div>
            ) : null}

            {supportsObjectScope ? (
              <div className="admin-rights-nested-subbox" style={{ marginTop: 20 }}>
                <div style={{ marginTop: 4, marginBottom: 8, fontWeight: 600 }}>Objektzuordnungen des Benutzers</div>
                <small style={{ color: "var(--text-soft)" }}>
                  Mit „Alle Objekte“ hat der Benutzer Vollzugriff im Mandanten. Mit „Nur ausgewählte Objekte“ wird der Zugriff begrenzt.
                </small>
                <div className="form-grid" style={{ marginTop: 8 }}>
                  <div className="form-field admin-object-scope-mode-field">
                    <label>Modus</label>
                    <select
                      value={selectedUserScope.mode}
                      disabled={!canEditRoleMatrix || objectScopeMutation.isPending}
                      onChange={(e) => {
                        const nextMode = e.target.value === "include" ? "include" : "all";
                        setScopeMessage("");
                        setScopeError("");
                        setObjectScopeDraft({
                          ...effectiveObjectScope,
                          userAssignments: {
                            ...effectiveObjectScope.userAssignments,
                            [activeSelectedUserId]: {
                              ...selectedUserScope,
                              mode: nextMode
                            }
                          }
                        });
                        if (nextMode === "include") {
                          setScopeObjectPage(1);
                        }
                      }}
                    >
                      <option value="all">Alle Objekte</option>
                      <option value="include">Ausgewählte Objekte</option>
                    </select>
                  </div>
                </div>

                {selectedUserScope.mode === "include" ? (
                  <div style={{ marginTop: 12 }}>
                    <div className="filterbar" style={{ marginBottom: 8 }}>
                      <SearchField
                        placeholder="Suche Objekt-Nr/Objektname"
                        value={scopeObjectSearch}
                        onChange={(value) => {
                          setScopeObjectSearch(value);
                          setScopeObjectPage(1);
                        }}
                      />
                    </div>
                    <small style={{ color: "var(--text-soft)" }}>
                      Zeilen: {pagedScopeObjects.length} / Gesamt: {filteredScopeObjects.length}
                    </small>
                    <div style={{ marginTop: 8, overflowX: "auto" }}>
                    <table className="data-table admin-rights-table">
                      <thead>
                        <tr>
                          <th style={{ width: 48, textAlign: "center" }}>Auswahl</th>
                          <th>Objekt-Nr</th>
                          <th>Objektname</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedScopeObjects.map((object) => {
                          const checked = selectedUserScope.objectIds.includes(object.id);
                          return (
                            <tr key={`scope-object-${object.id}`}>
                              <td style={{ textAlign: "center" }}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  disabled={!canEditRoleMatrix || objectScopeMutation.isPending}
                                  onChange={(e) => {
                                    const nextObjectIds = e.target.checked
                                      ? Array.from(new Set([...selectedUserScope.objectIds, object.id]))
                                      : selectedUserScope.objectIds.filter((id) => id !== object.id);
                                    setScopeMessage("");
                                    setScopeError("");
                                    setObjectScopeDraft({
                                      ...effectiveObjectScope,
                                      userAssignments: {
                                        ...effectiveObjectScope.userAssignments,
                                        [activeSelectedUserId]: {
                                          ...selectedUserScope,
                                          objectIds: nextObjectIds
                                        }
                                      }
                                    });
                                  }}
                                />
                              </td>
                              <td>{object.objektnummer}</td>
                              <td>{object.objektname}</td>
                            </tr>
                          );
                        })}
                        {!pagedScopeObjects.length ? (
                          <tr>
                            <td colSpan={3}>Keine Objekte gefunden.</td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                    </div>
                    <div
                      className="pagination"
                      style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}
                    >
                      <button
                        type="button"
                        disabled={scopeObjectCurrentPage <= 1}
                        onClick={() => setScopeObjectPage((p) => Math.max(1, p - 1))}
                      >
                        Zurück
                      </button>
                      <span style={{ fontSize: 13 }}>
                        Seite {scopeObjectCurrentPage} / {scopeObjectTotalPages}
                      </span>
                      <button
                        type="button"
                        disabled={scopeObjectCurrentPage >= scopeObjectTotalPages}
                        onClick={() => setScopeObjectPage((p) => Math.min(scopeObjectTotalPages, p + 1))}
                      >
                        Weiter
                      </button>
                    </div>
                  </div>
                ) : null}

                <div className="form-actions">
                  <button
                    type="button"
                    disabled={!canEditRoleMatrix || !objectScopeDraft || objectScopeMutation.isPending}
                    onClick={() => {
                      if (!objectScopeDraft) return;
                      objectScopeMutation.mutate(objectScopeDraft);
                    }}
                  >
                    {objectScopeMutation.isPending ? "Speichere..." : "Objektzuordnung speichern"}
                  </button>
                  {scopeError ? <span className="error-text">{scopeError}</span> : null}
                  {scopeMessage ? <span>{scopeMessage}</span> : null}
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="admin-rights-user-override-note">Bitte zuerst einen Benutzer auswählen.</div>
        )}
      </section>

      <DeleteCodeConfirmModal
        open={Boolean(userDeleteConfirm)}
        title="Benutzer löschen"
        message={
          userDeleteConfirm
            ? `Soll der Benutzer "${userDeleteConfirm.label}" wirklich gelöscht werden?`
            : ""
        }
        code={userDeleteConfirm?.code ?? "0000"}
        input={userDeleteConfirm?.input ?? ["", "", "", ""]}
        error={userDeleteConfirm?.error}
        isPending={deleteMutation.isPending}
        onClose={() => {
          if (deleteMutation.isPending) return;
          setUserDeleteConfirm(null);
        }}
        onInputChange={(next) =>
          setUserDeleteConfirm((prev) => (prev ? { ...prev, input: next, error: "" } : prev))
        }
        onErrorReset={() =>
          setUserDeleteConfirm((prev) => (prev ? { ...prev, error: "" } : prev))
        }
        onConfirm={() => {
          if (!userDeleteConfirm) return;
          const entered = userDeleteConfirm.input.join("");
          if (entered !== userDeleteConfirm.code) {
            setUserDeleteConfirm((prev) => (prev ? { ...prev, error: "Sicherheitszahl stimmt nicht." } : prev));
            return;
          }
          deleteMutation.mutate(userDeleteConfirm.id);
        }}
        confirmLabel="Benutzer löschen"
        pendingLabel="Lösche Benutzer..."
      />

      <DeleteCodeConfirmModal
        open={Boolean(groupDeleteConfirm)}
        title="Benutzergruppe löschen"
        message={
          groupDeleteConfirm
            ? `Soll die Benutzergruppe "${groupDeleteConfirm.label}" wirklich gelöscht werden?`
            : ""
        }
        code={groupDeleteConfirm?.code ?? "0000"}
        input={groupDeleteConfirm?.input ?? ["", "", "", ""]}
        error={groupDeleteConfirm?.error}
        onClose={() => setGroupDeleteConfirm(null)}
        onInputChange={(next) =>
          setGroupDeleteConfirm((prev) => (prev ? { ...prev, input: next, error: "" } : prev))
        }
        onErrorReset={() =>
          setGroupDeleteConfirm((prev) => (prev ? { ...prev, error: "" } : prev))
        }
        onConfirm={() => {
          if (!groupDeleteConfirm) return;
          const entered = groupDeleteConfirm.input.join("");
          if (entered !== groupDeleteConfirm.code) {
            setGroupDeleteConfirm((prev) => (prev ? { ...prev, error: "Sicherheitszahl stimmt nicht." } : prev));
            return;
          }
          const nextGroups = effectiveGroupState.groups.filter((entry) => entry.id !== groupDeleteConfirm.id);
          const nextAssignments = Object.fromEntries(
            Object.entries(effectiveGroupState.userAssignments).map(([userId, assignment]) => [
              userId,
              {
                ...assignment,
                groupIds: assignment.groupIds.filter((id) => id !== groupDeleteConfirm.id)
              }
            ])
          );
          setGroupsMessage("");
          setGroupsError("");
          setGroupsDraft({ groups: nextGroups, userAssignments: nextAssignments });
          setSelectedGroupId(nextGroups[0]?.id ?? "");
          setGroupDeleteConfirm(null);
        }}
        confirmLabel="Benutzergruppe löschen"
      />
    </div>
  );
};

